import {
  InterventionReport,
  InterventionCompletionStatus,
  ReviewStatus,
} from '@inplay/models';
import moment from 'moment';

export class InterventionReportModel {
  private readonly data: InterventionReport;

  constructor(data: InterventionReport) {
    this.data = data;
  }

  /**
   * Access the underlying remote data shape.
   * USE ONLY for: form pre-population, API request payloads, unmigrated utility functions.
   * DO NOT use for display logic in table cells — use computed getters instead.
   */
  get raw(): InterventionReport {
    return this.data;
  }

  get originalReportId(): string | undefined {
    return this.data.originalReportId;
  }

  get id(): string | undefined {
    return (
      ((this.data as Record<string, unknown>)._id as string | undefined) ??
      this.data.originalReportId
    );
  }

  get reportRefNo(): string {
    return this.data.reportRefNo;
  }

  get harmReport(): InterventionReport['harmReport'] {
    return this.data.harmReport;
  }

  get createdAt(): InterventionReport['createdAt'] {
    return this.data.createdAt;
  }

  get playerProfile(): InterventionReport['playerProfile'] {
    return this.data.playerProfile;
  }

  get playerDetails(): InterventionReport['playerDetails'] {
    return this.data.playerDetails;
  }

  get signsOfGamblingHarm(): InterventionReport['signsOfGamblingHarm'] {
    return this.data.signsOfGamblingHarm;
  }

  get conductedBy(): InterventionReport['conductedBy'] {
    return this.data.conductedBy;
  }

  get reviewedBy(): InterventionReport['reviewedBy'] {
    return this.data.reviewedBy;
  }

  get followUpAction(): InterventionReport['followUpAction'] {
    return this.data.followUpAction;
  }

  get harmInstances(): number | undefined {
    return this.data.harmInstances;
  }

  get interventionAction(): string[] {
    return this.data.interventionAction ?? [];
  }

  get staffComplied(): boolean | undefined {
    return this.data.staffComplied;
  }

  get interventionCountForPlayer(): string {
    return this.data.interventionCountForPlayer;
  }

  get interventionStatus(): InterventionReport['interventionStatus'] {
    return this.data.interventionStatus;
  }

  get reviewStatus(): InterventionReport['reviewStatus'] {
    return this.data.reviewStatus;
  }

  get venueBranch(): InterventionReport['venueBranch'] {
    return this.data.venueBranch;
  }

  get version(): number {
    return this.data.version;
  }

  get status(): InterventionReport['status'] {
    return this.data.status;
  }

  get isAutoGenerated(): boolean {
    return this.data.conductedBy?.firstName === 'System';
  }

  get isReviewed(): boolean {
    return Boolean(this.data.reviewedBy);
  }

  get conductedByFullName(): string {
    if (!this.data.conductedBy) {
      return '-';
    }
    return `${this.data.conductedBy.firstName} ${this.data.conductedBy.lastName}`.trim();
  }

  get reviewedByFullName(): string {
    if (!this.data.reviewedBy) {
      return '-';
    }
    return `${this.data.reviewedBy.firstName} ${this.data.reviewedBy.lastName}`.trim();
  }

  get playerIdShort(): string {
    return this.data.playerProfile?.id?.slice(0, 5) ?? '-';
  }

  get createdAtFormatted(): string {
    if (!this.data.createdAt) {
      return '-';
    }
    return moment(this.data.createdAt).format('hh:mm A, DD/MM/YYYY');
  }

  get completionStatus(): 'complete' | 'missed' | 'unknown' {
    switch (this.data.interventionStatus) {
      case InterventionCompletionStatus.COMPLETE:
        return 'complete';
      case InterventionCompletionStatus.MISSED:
        return 'missed';
      default:
        return 'unknown';
    }
  }

  // Used by view layer to map to CSS dot colors via REVIEW_STATUS_DOT_COLORS constant.
  // Consumer: interventionReportConfig.ts (pending migration from getInterventionReportRevieweStatusDotColor helper).
  get reviewCompletionStatus():
    | 'pending'
    | 'complete'
    | 'missed'
    | 'not_applicable'
    | 'unknown' {
    switch (this.data.reviewStatus) {
      case ReviewStatus.PENDING:
        return 'pending';
      case ReviewStatus.COMPLETE:
        return 'complete';
      case ReviewStatus.MISSED:
        return 'missed';
      case ReviewStatus.NOT_APPLICABLE:
        return 'not_applicable';
      default:
        return 'unknown';
    }
  }

  get harmSignsCount(): number {
    const gamblingHarmSigns: InterventionReport['signsOfGamblingHarm'] =
      this.data.signsOfGamblingHarm;
    if (!gamblingHarmSigns) {
      return 0;
    }
    return (
      (gamblingHarmSigns.lengthOfPlay?.length ?? 0) +
      (gamblingHarmSigns.money?.length ?? 0) +
      (gamblingHarmSigns.behaviour?.length ?? 0) +
      (gamblingHarmSigns.other?.length ?? 0)
    );
  }

  get searchableFields(): (string | undefined)[] {
    const conductedByFullNameForSearch: string | undefined = this.data
      .conductedBy
      ? `${this.data.conductedBy.firstName} ${this.data.conductedBy.lastName}`.trim()
      : undefined;

    return [
      this.data.reportRefNo,
      this.data.playerProfile?.id,
      conductedByFullNameForSearch,
      this.data.conductedBy?.firstName,
      this.data.conductedBy?.lastName,
      this.data.playerDetails?.description,
      ...(this.data.harmReport?.map(
        (linkedReport: { id: string; refNo: string }) => linkedReport.refNo,
      ) ?? []),
    ];
  }
}
